import { useEffect, useRef, useState } from "react";

interface EditableEntityProps {
  value: string;
  onUpdate: (newValue: string) => void;
}

type ParsedSegment =
  | { type: "text"; content: string }
  | { type: "entity"; content: string; entityIndex: number };

export const parseTextWithEntities = (text: string): ParsedSegment[] => {
  const segments: ParsedSegment[] = [];
  const regex = /`([^`]+)`/g;
  let lastIndex = 0;
  let entityIndex = 0;
  let match = regex.exec(text);

  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        content: text.slice(lastIndex, match.index),
      });
    }
    segments.push({
      type: "entity",
      content: match[1],
      entityIndex: entityIndex++,
    });
    lastIndex = regex.lastIndex;
    match = regex.exec(text);
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.slice(lastIndex) });
  }

  return segments;
};

export const updateEntityInText = (
  text: string,
  entityIndexToUpdate: number,
  newValue: string
): string => {
  let currentEntityIndex = 0;
  return text.replace(/`([^`]+)`/g, (match) => {
    if (currentEntityIndex === entityIndexToUpdate) {
      currentEntityIndex++;
      return `\`${newValue}\``;
    }
    currentEntityIndex++;
    return match;
  });
};

const EditableEntity: React.FC<EditableEntityProps> = ({ value, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    if (text !== value) {
      onUpdate(text);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSave();
          } else if (e.key === "Escape") {
            setIsEditing(false);
            setText(value);
          }
        }}
        className="w-full px-1 py-0.5 border border-blue-500 rounded-md bg-white text-sm outline-none shadow-sm"
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      onClick={() => setIsEditing(true)}
      className="bg-blue-100 text-blue-800 rounded-md px-1 py-0.5 cursor-pointer hover:bg-blue-200 transition-colors"
      title="Click to edit"
    >
      {value}
    </span>
  );
};

export default EditableEntity;
